/**
 * Drill-Down Modal Component for Admin Control Panel
 * 
 * Displays detailed, paginated, sortable, searchable user list.
 */
import React, { useState, useEffect, useCallback } from "react";
import { 
  Search, ChevronLeft, ChevronRight, ArrowUpDown, 
  ArrowUp, ArrowDown, Loader2, X, Users
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { API_URL } from '@/config/api';

const DrillDownModal = ({ open, onOpenChange, title, filterType, dateRange }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [hasMore, setHasMore] = useState(false);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1); // Reset to first page on search
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchUsers = useCallback(async () => {
    if (!filterType || !dateRange) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({
        filter_type: filterType,
        start_date: dateRange.start,
        end_date: dateRange.end,
        page: page.toString(),
        page_size: pageSize.toString(),
        sort_by: sortBy,
        sort_order: sortOrder
      });
      
      if (debouncedSearch) {
        params.append('search', debouncedSearch);
      }
      
      const response = await fetch(
        `${API_URL}/api/admin/analytics/users/list?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch users');
      }
      
      const data = await response.json();
      setUsers(data.users);
      setTotal(data.total);
      setHasMore(data.has_more);
    } catch (err) {
      console.error('Error fetching user list:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filterType, dateRange, page, pageSize, sortBy, sortOrder, debouncedSearch]);

  useEffect(() => {
    if (open) {
      fetchUsers();
    }
  }, [open, fetchUsers]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setPage(1);
      setSearch('');
      setDebouncedSearch('');
      setSortBy('created_at');
      setSortOrder('desc');
    }
  }, [open]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setPage(1);
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) {
      return <ArrowUpDown className="w-4 h-4 text-slate-500" />;
    }
    return sortOrder === 'asc' 
      ? <ArrowUp className="w-4 h-4 text-blue-400" />
      : <ArrowDown className="w-4 h-4 text-blue-400" />;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatListenTime = (seconds) => {
    if (!seconds || seconds === 0) return '—';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="max-w-4xl max-h-[90vh] bg-slate-900 border-slate-700 overflow-hidden flex flex-col"
        allowClickOutside={true}
        hideCloseButton={true}
      >
        {/* Close Button - positioned in top right */}
        <button
          onClick={() => onOpenChange(false)}
          className="absolute right-4 top-4 p-1 rounded-md text-slate-400 hover:text-white hover:bg-slate-800 transition-colors z-10"
          data-testid="close-modal-btn"
        >
          <X className="w-5 h-5" />
          <span className="sr-only">Close</span>
        </button>

        <DialogHeader className="shrink-0 pr-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-900/50">
                <Users className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <DialogTitle className="text-xl font-bold text-white">
                  {title}
                </DialogTitle>
                <p className="text-sm text-slate-400 mt-1">
                  {total.toLocaleString()} users • {dateRange?.start} to {dateRange?.end}
                </p>
              </div>
            </div>
          </div>
        </DialogHeader>

        {/* Search Bar */}
        <div className="relative shrink-0 my-4">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
            data-testid="user-search-input"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Table Container */}
        <div className="flex-1 overflow-auto min-h-0">
          {loading && users.length === 0 ? (
            <div className="flex justify-center items-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <p className="text-red-400">{error}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchUsers}
                className="mt-4 border-red-700 text-red-400 hover:bg-red-900/30"
              >
                Retry
              </Button>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-16">
              <Users className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No users found</p>
              {debouncedSearch && (
                <p className="text-sm text-slate-500 mt-2">
                  Try a different search term
                </p>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader className="sticky top-0 bg-slate-900">
                <TableRow className="border-slate-700 hover:bg-transparent">
                  <TableHead 
                    className="text-slate-300 font-bold cursor-pointer hover:text-white transition-colors"
                    onClick={() => handleSort('email')}
                  >
                    <div className="flex items-center gap-2">
                      Email
                      {getSortIcon('email')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-slate-300 font-bold cursor-pointer hover:text-white transition-colors"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center gap-2">
                      Name
                      {getSortIcon('name')}
                    </div>
                  </TableHead>
                  <TableHead className="text-slate-300 font-bold">
                    Role
                  </TableHead>
                  <TableHead 
                    className="text-slate-300 font-bold cursor-pointer hover:text-white transition-colors"
                    onClick={() => handleSort('created_at')}
                  >
                    <div className="flex items-center gap-2">
                      Registered
                      {getSortIcon('created_at')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-slate-300 font-bold cursor-pointer hover:text-white transition-colors"
                    onClick={() => handleSort('last_login_at')}
                  >
                    <div className="flex items-center gap-2">
                      Last Login
                      {getSortIcon('last_login_at')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-slate-300 font-bold cursor-pointer hover:text-white transition-colors text-right"
                    onClick={() => handleSort('total_listen_seconds')}
                  >
                    <div className="flex items-center justify-end gap-2">
                      Listen Time
                      {getSortIcon('total_listen_seconds')}
                    </div>
                  </TableHead>
                  <TableHead className="text-slate-300 font-bold text-right">
                    Library
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow 
                    key={user.id} 
                    className="border-slate-800 hover:bg-slate-800/50"
                    data-testid={`user-row-${user.id}`}
                  >
                    <TableCell className="text-white font-medium">
                      {user.email}
                    </TableCell>
                    <TableCell className="text-slate-300">
                      {user.name || '—'}
                    </TableCell>
                    <TableCell>
                      <span className={`
                        px-2 py-1 rounded text-xs font-medium
                        ${user.role === 'admin' ? 'bg-amber-900/50 text-amber-300' : ''}
                        ${user.role === 'publisher' ? 'bg-purple-900/50 text-purple-300' : ''}
                        ${user.role === 'author' ? 'bg-blue-900/50 text-blue-300' : ''}
                        ${user.role === 'reader' ? 'bg-slate-700 text-slate-300' : ''}
                      `}>
                        {user.role}
                      </span>
                    </TableCell>
                    <TableCell className="text-slate-400">
                      {formatDate(user.created_at)}
                    </TableCell>
                    <TableCell className="text-slate-400">
                      {formatDate(user.last_login_at)}
                    </TableCell>
                    <TableCell className="text-slate-300 text-right">
                      {formatListenTime(user.total_listen_seconds)}
                    </TableCell>
                    <TableCell className="text-slate-300 text-right">
                      {user.books_in_library || 0}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Pagination */}
        {total > pageSize && (
          <div className="shrink-0 flex items-center justify-between pt-4 border-t border-slate-800">
            <p className="text-sm text-slate-400">
              Showing {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, total)} of {total}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1 || loading}
                className="border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-50"
                data-testid="prev-page-btn"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-slate-400 px-2">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => p + 1)}
                disabled={!hasMore || loading}
                className="border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-50"
                data-testid="next-page-btn"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Loading overlay for pagination */}
        {loading && users.length > 0 && (
          <div className="absolute inset-0 bg-slate-900/50 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DrillDownModal;
